import { useState } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { Button, Field, FieldLabel, Select, Switch, Textarea, Typography } from "@akuna/ui";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useToast } from "@/hooks/use-toast";
import { useCreateStayActivity } from "../activities/useActivityMutations";
import { useStayActivityTypes } from "../activities/useStayActivityTypes";

export interface InlinePetActivityFormProps {
  petId: string;
  day: string;
  /** Quem abre é o "+" do cabeçalho da seção, igual ao de Observações. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const dateAtLocalTime = (day: string) => {
  const [year, month, date] = day.split("-").map(Number);
  const now = new Date();
  return new Date(year, month - 1, date, now.getHours(), now.getMinutes(), 0, 0);
};

export function InlinePetActivityForm({ petId, day, open, onOpenChange }: InlinePetActivityFormProps) {
  const { t } = useTranslation("daycareHotel");
  const { toast } = useToast();
  const types = useStayActivityTypes();
  const create = useCreateStayActivity();
  const [selectedTypeId, setSelectedTypeId] = useState("");
  const [description, setDescription] = useState("");
  const [selectedDateTime, setSelectedDateTime] = useState(() => dateAtLocalTime(day));
  const [isRecurring, setIsRecurring] = useState(false);
  const defaultType = types.data?.find((type) => type.icon === "activity") ?? types.data?.[0];
  const effectiveTypeId = selectedTypeId || defaultType?.id || "";
  const selectedType = types.data?.find((type) => type.id === effectiveTypeId);
  const typeOptions = (types.data ?? []).map((type) => ({ value: type.id, label: type.name }));
  const scheduledDate = format(selectedDateTime, "yyyy-MM-dd");
  const scheduledTime = format(selectedDateTime, "HH:mm");
  const canSubmit = Boolean(description.trim() && scheduledTime && selectedType) && !create.isPending;

  const reset = () => {
    setDescription("");
    setSelectedDateTime(dateAtLocalTime(day));
    setIsRecurring(false);
  };

  const cancel = () => {
    reset();
    onOpenChange(false);
  };

  const submit = async () => {
    if (!description.trim() || !scheduledTime) return;
    if (!selectedType) {
      toast({ title: t("panel.activityTypeUnavailable"), variant: "destructive" });
      return;
    }

    try {
      await create.mutateAsync({
        targetType: "pet",
        targetPetId: petId,
        targetPackId: null,
        typeId: selectedType.id,
        scheduledTime,
        description: description.trim(),
        assignedTo: null,
        scheduledDate,
        recurrence: isRecurring
          ? { frequency: "daily", intervalDays: null, daysOfWeek: [], daysOfMonth: [], endsOn: null }
          : null,
      });
      reset();
      onOpenChange(false);
      toast({ title: t("panel.activityCreated") });
    } catch {
      toast({ title: t("panel.activityCreateError"), variant: "destructive" });
    }
  };

  if (!open) return null;

  return (
    <div className="grid gap-2.5 rounded-lg border border-border bg-muted p-2.5">

      <Field>
        <FieldLabel>{t("panel.observationDateTime")}</FieldLabel>
        <Popover>
          <PopoverTrigger asChild>
            <Button type="button" variant="outline" className="w-full justify-start text-left font-normal">
              {format(selectedDateTime, "dd/MM/yyyy 'às' HH:mm")}
              <CalendarIcon className="ml-auto h-4 w-4 opacity-50" aria-hidden="true" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-full p-0" align="start">
            <div className="flex flex-col gap-4 p-3 sm:flex-row sm:items-start">
              <Calendar
                mode="single"
                selected={selectedDateTime}
                defaultMonth={selectedDateTime}
                onSelect={(date) => {
                  if (!date) return;
                  setSelectedDateTime((current) => new Date(date.getFullYear(), date.getMonth(), date.getDate(), current.getHours(), current.getMinutes(), 0, 0));
                }}
                initialFocus
              />
              <div className="flex min-w-[160px] flex-col gap-2">
                <FieldLabel htmlFor="inline-activity-time">{t("panel.activityTimeLabel")}</FieldLabel>
                <input
                  id="inline-activity-time"
                  type="time"
                  value={scheduledTime}
                  onChange={(event) => {
                    const [hours, minutes] = event.target.value.split(":").map(Number);
                    if (Number.isNaN(hours) || Number.isNaN(minutes)) return;
                    setSelectedDateTime((current) => new Date(current.getFullYear(), current.getMonth(), current.getDate(), hours, minutes, 0, 0));
                  }}
                  className="akuna-control w-full"
                  required
                />
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </Field>
      <Select
        label={t("panel.activityTypeLabel")}
        options={typeOptions}
        value={effectiveTypeId}
        onValueChange={setSelectedTypeId}
      />
      <Field>
        <FieldLabel htmlFor="inline-activity-description">{t("panel.activityDescriptionLabel")}</FieldLabel>
        <Textarea
          id="inline-activity-description"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          placeholder={t("panel.activityDescriptionPlaceholder")}
        />
      </Field>
      <div className="flex items-center gap-2">
        <Switch
          id="inline-activity-recurring"
          checked={isRecurring}
          onCheckedChange={setIsRecurring}
        />
        <FieldLabel htmlFor="inline-activity-recurring" className="cursor-pointer text-sm font-normal">
          {t("panel.recurringActivityLabel")}
        </FieldLabel>
      </div>
      {!types.isLoading && !selectedType && (
        <Typography variant="caption" className="mt-2 block text-state-danger">
          {t("panel.activityTypeUnavailable")}
        </Typography>
      )}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={cancel}>
          {t("panel.cancelActivity")}
        </Button>
        <Button type="button" size="sm" disabled={!canSubmit} loading={create.isPending} onClick={submit}>
          {t("panel.saveActivity")}
        </Button>
      </div>
    </div>
  );
}
